import React from 'react';
import { Box, Chip, Table, Sheet, Typography } from '@mui/joy';
import { calculateOffset, getPopularityIndex, getSeasonalityIndex } from 'helpers/Functions';
import { Popover } from '@mui/material';
import { Colors } from 'helpers/Colors';
import { Gauge, gaugeClasses } from '@mui/x-charts';
import { PiLeafFill } from 'react-icons/pi';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import GroupsIcon from '@mui/icons-material/Groups';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import StarIcon from '@mui/icons-material/Star';
import DiamondIcon from '@mui/icons-material/Diamond';
import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import FmdBadIcon from '@mui/icons-material/FmdBad';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarOutlineOutlinedIcon from '@mui/icons-material/StarOutlineOutlined';

export const generateCostLabel = (toDestination) => {
    const cost = toDestination?.scores?.cost_label;
    return (
        <Chip
            component="span"
            size="sm"
            variant="soft"
            color={cost !== undefined ? (cost < 3 ? 'success' : cost == 5 ? 'danger' : 'warning') : 'neutral'}
        >
            {(() => {
                switch (cost) {
                    case 1:
                    case 2:
                        return '$';
                    case 3:
                    case 4:
                        return '$$';
                    case 5:
                        return '$$$';
                    default:
                        return 'No data';
                }
            })()}
        </Chip>
    );
}

export const generateRatingLabel = (toDestination) => {
    const rating = Math.round(toDestination?.scores?.total_score);

    let starCount = 0;
    let hasHalfStar = false;

    switch (rating) {
        case 1:
            starCount = 1;
            break;
        case 2:
            starCount = 1;
            hasHalfStar = true;
            break;
        case 3:
            starCount = 2;
            break;
        case 4:
            starCount = 2;
            hasHalfStar = true;
            break;
        case 5:
            starCount = 3;
            break;
        default:
            return 'No data'; // Return 'No data' if rating is 0 or undefined
    }

    return (
        <>
            {/* Render full stars */}
            {Array.from({ length: starCount }, (_, i) => (
                <StarIcon key={i} fontSize='small' color='warning' />
            ))}

            {/* Render half star if needed */}
            {hasHalfStar && <StarHalfIcon fontSize='small' color='warning' />}

            {/* Render remaining empty stars */}
            {Array.from({ length: 3 - starCount - (hasHalfStar ? 1 : 0) }, (_, i) => (
                <StarOutlineOutlinedIcon key={`empty-${i}`} fontSize='small' color='warning' />
            ))}
        </>
    );
};

export const generatePopularityLabel = (toDestination) => {
    const popularityScore = toDestination?.popularity?.popularity_score
    const popIndex = getPopularityIndex(popularityScore);
    switch (popIndex) {
        case 0:
            return (
                <Typography
                    justifyContent='center'
                    level='body-xs'
                    color='success'
                    startDecorator={<DiamondIcon sx={{ color: 'success' }} />}
                >
                    Rare Find
                </Typography>
            );
        case 1:
            return (
                <Typography
                    justifyContent='center'
                    level='body-xs'
                    color='warning'
                    startDecorator={<AutoGraphIcon sx={{ color: 'warning' }} />}
                >
                    Rising
                </Typography>
            );
        case 2:
            return (
                <Typography
                    sx={{ color: Colors.brown }}
                    justifyContent='center'
                    level='body-xs'
                    startDecorator={<FmdBadIcon sx={{ color: 'warning' }} />}
                >
                    Traffic
                </Typography>
            );
        case 3:
            return (
                <Typography
                    justifyContent='center'
                    level='body-xs'
                    color='danger'
                    startDecorator={<ReportGmailerrorredIcon sx={{ color: 'error' }} />}
                >
                    Hotspot
                </Typography>
            );
        default:
            return (
                <Typography justifyContent='center' level='body-xs' color='neutral'>
                    No data
                </Typography>
            );
    }
};

const CityCardTable = ({ toDestination, month, minCo2Mode, averages, userInterests, cityInterests,  }) => {

    const co2AbsorbedPerTreePerDay = 0.0164;
    const [anchorEl, setAnchorEl] = React.useState(null);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const open = Boolean(anchorEl);
    const id = open ? 'simple-popover' : undefined;
    const distance = minCo2Mode?.distance_km ? Math.round(minCo2Mode?.distance_km) + " Km" : "NA"
    const offset = calculateOffset(
        'emission',
        averages,
        minCo2Mode.co2,
        toDestination.popularity.popularity_score,
        toDestination.seasonality[month]
    );

    const generateInterestLabel = () => {
        const filteredInterests = userInterests?.filter(value => cityInterests.includes(value));
        const interestMatch = filteredInterests ? Math.round(filteredInterests?.length * 100 / (userInterests.length)) : 50;
        return (
            <>
            <Chip
                component="span"
                size="sm"
                variant="soft"
                color={interestMatch > 50 ? 'success' : 'warning'}
            >
                {interestMatch}% 
            </Chip> Match
            </>
        );
    };

    const calculateNumberOfTrees = (totalCO2e, percentReduction) => {
        // Convert percentage to decimal
        let reductionFraction = percentReduction / 100;

        // Calculate the amount of CO2e reduced
        let reducedCO2e = totalCO2e * reductionFraction;

        // CO2 absorbed by one tree per day in kg (previously calculated as 0.0164 kg)

        // Calculate the number of trees required
        let numberOfTrees = reducedCO2e / co2AbsorbedPerTreePerDay;

        return Math.abs(Math.round(numberOfTrees)); // rounding to the nearest tree
    };

    const generateEmissionInfo = (offset) => {
        return (
            <>
                <InfoOutlinedIcon fontSize='14' aria-describedby={id} onClick={handleClick} sx={{ cursor: "pointer" }} />
                <Popover
                    id={id}
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "center",
                    }}
                >
                    <Box sx={{ p: 2, maxWidth: 300 }}>
                        <Typography>
                            This is equivalent to the CO₂ absorption of approximately {calculateNumberOfTrees(averages.co2, offset)} trees in one day.
                        </Typography>
                        {/* <Typography variant="caption" color="textSecondary">
                            (Based on EPA's 10-year tree carbon sequestration estimate)
                        </Typography> */}
                    </Box>
                </Popover>
            </>
        );
    };

    const generateEmissionLabel = (offset) => {
        return (
            <Box sx={{ display: "flex", justifyContent: "center" }}>
            <Gauge width={90} height={100} valueMin={0} valueMax={125} startAngle={-90} endAngle={360} text={offset !== undefined ? ((offset > 0 ? '+' : '') + String(offset) + '%') : ("NA")} value={offset ? Math.abs(offset) : 0}
                sx={{
                    [`& .${gaugeClasses.valueText}`]: {
                        transform: 'translate(0px, 0px)',
                        fontSize: 16,
                        color: 'red',
                    },
                    [`& .${gaugeClasses.valueArc}`]: offset > 0 ? {

                        fill: Colors.red,
                    } : {

                        fill: Colors.green,
                    },
                }}
            />
        </Box>
        );
    }

    const generateSeasonalityLabel = (seasonalityScore) => {
        const seasonalityIndex = getSeasonalityIndex(seasonalityScore);
        switch (seasonalityIndex) {
            case 0:
                return (
                    <Typography
                        justifyContent='center'
                        level='body-xs'
                        color='success'
                        startDecorator={<PiLeafFill style={{ color: 'green' }} />}
                    >
                        Quiet
                    </Typography>
                );
            case 1:
                return (
                    <Typography
                        justifyContent='center'
                        level='body-xs'
                        color='warning'
                        startDecorator={<PersonIcon sx={{ color: 'warning' }} />}
                    >
                        Off-Peak
                    </Typography>
                );
            case 2:
                return (
                    <Typography
                        sx={{ color: Colors.brown }}
                        justifyContent='center'
                        level='body-xs'
                        startDecorator={<PeopleIcon sx={{ color: 'warning' }} />}
                    >
                        Busy
                    </Typography>
                );
            case 3:
                return (
                    <Typography
                        justifyContent='center'
                        level='body-xs'
                        color='danger'
                        startDecorator={<GroupsIcon sx={{ color: 'error' }} />}
                    >
                        Crowded
                    </Typography>
                );
            default:
                return (
                    <Typography justifyContent='center' level='body-xs' color='neutral'>
                        No data
                    </Typography>
                );
        }
    };

    const generateTemperatureLabel = () => {
        const tempData = toDestination?.temperature;
        let high, low;
        if (tempData.length > 0) {
            const temp = tempData.filter(entry => entry.month === month);
            if (temp.length > 0) {
                ({ high, low } = temp[0]);
            }
        }
        return (
            <>
                {(high && low) ?
                    (<>{low}°C - {high}°C </>)
                    :
                    <>No data</>
                }
            </>
        );
    };

    return (
        <Sheet variant='soft'>
            <Table borderAxis='none' variant='plain' color='neutral' size='sm' stickyHeader sx={{ textAlign: 'center' }}>
                <tbody>
                    <tr>
                        <th style={{ textAlign: 'center' }}>Popularity</th>
                        <th style={{ textAlign: 'center' }}>Emission
                            {generateEmissionInfo(offset)}
                        </th>
                        <th style={{ textAlign: 'center' }}>Seasonality</th>
                    </tr>
                    <tr>
                        <td>{generatePopularityLabel(toDestination)}</td>
                        <td rowSpan="3" style={{ textAlign: 'center', verticalAlign: 'middle' }}>
                            {generateEmissionLabel(offset)}
                        </td>
                        <td>{generateSeasonalityLabel(toDestination.seasonality[month])}</td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: 'center' }}>Cost</th>
                        <th style={{ textAlign: 'center' }}>Rating</th>
                    </tr>
                    <tr>
                        <td>{generateCostLabel(toDestination)}</td>
                        <td>{generateRatingLabel(toDestination)}</td>
                    </tr>
                    <tr>
                        <th style={{ textAlign: 'center' }}>Distance</th>
                        <th style={{ textAlign: 'center' }}>Temperature</th>
                        <th style={{ textAlign: 'center' }}>Interest</th>
                    </tr>
                    <tr>
                        <td>{distance}</td>
                        <td>{generateTemperatureLabel()}</td>
                        <td>{generateInterestLabel()}</td>
                    </tr>
                </tbody>

            </Table>
        </Sheet>
    );
};

export default CityCardTable;
